import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from "react";
import { completionSummaryText, getItemCompletionState, isBuyThereItem, isFamilyChecklistKind, isItemFullyComplete, isMyFamilyChecked, toggleMyFamilyChecked, } from "@/lib/planAssign";
import { ItemDetailModal } from "./ItemDetailModal";
import { useCheckboxAction } from "./useCheckboxAction";
import { getCollectionDef } from "./CollectionsTab";
/**
 * One collection row. Lives in its own component so the shared checkbox hook can
 * obey the rules of hooks (a collection can contain items that aren't mine, so a
 * tap may open the take-responsibility choice sheet instead of toggling).
 */
function CollectionRow({ loc, participants, myDisplayName, myParticipantId, onUpdate, onOpenDetail, }) {
    const { item, sectionTitle, sectionIcon, subTitle, sectionKind } = loc;
    const familyChecklist = isFamilyChecklistKind(sectionKind);
    const { onCheckboxTap, sheet } = useCheckboxAction(item, myParticipantId, myDisplayName, participants, onUpdate);
    const myFamilyChecked = isMyFamilyChecked(item, myParticipantId, myDisplayName);
    // Family-checklist rows show/toggle the current family's own packing state.
    const completion = familyChecklist
        ? myFamilyChecked
            ? "complete"
            : "none"
        : getItemCompletionState(item);
    const fullyComplete = familyChecklist ? myFamilyChecked : isItemFullyComplete(item);
    const summary = familyChecklist
        ? myFamilyChecked
            ? "נארז אצלנו"
            : ""
        : completionSummaryText(item);
    const handleCheckboxTap = familyChecklist
        ? () => onUpdate(toggleMyFamilyChecked(item, myParticipantId, myDisplayName, participants))
        : onCheckboxTap;
    const origin = [sectionIcon, sectionTitle || "(ללא שם)", subTitle].filter(Boolean).join(" · ");
    return (_jsxs("li", { className: "flex items-center gap-3 rounded-2xl border border-slate-100 bg-white px-4 py-3 shadow-sm", children: [_jsxs("button", { onClick: handleCheckboxTap, role: "checkbox", "aria-checked": completion === "complete" ? "true" : completion === "partial" ? "mixed" : "false", "aria-label": completion === "complete"
                    ? "בטל סימון"
                    : completion === "partial"
                        ? "בוצע חלקית"
                        : "סמן כבוצע", className: `flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition-all ${completion === "complete"
                    ? "border-emerald-500 bg-emerald-500 text-white"
                    : completion === "partial"
                        ? "border-emerald-500 bg-white text-emerald-500"
                        : "border-slate-300 hover:border-emerald-400"}`, children: [completion === "complete" && (_jsx("svg", { width: "10", height: "10", viewBox: "0 0 12 12", fill: "none", "aria-hidden": true, children: _jsx("path", { d: "M2 6l3 3 5-5", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" }) })), completion === "partial" && (_jsx("svg", { width: "12", height: "12", viewBox: "0 0 12 12", "aria-hidden": true, children: _jsx("path", { d: "M6 0 A6 6 0 0 1 6 12 Z", fill: "currentColor" }) }))] }), _jsxs("button", { onClick: onOpenDetail, className: "flex-1 min-w-0 text-right", children: [_jsxs("div", { className: "flex items-center gap-1.5", children: [_jsx("span", { className: `truncate text-sm ${fullyComplete ? "text-slate-400 line-through" : "text-slate-700"}`, children: item.text }), isBuyThereItem(item) && (_jsx("span", { className: "shrink-0 rounded-full bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-600", children: "\u05DC\u05E7\u05E0\u05D5\u05EA \u05E9\u05DD" })), item.quantity && (_jsxs("span", { className: "shrink-0 text-xs text-slate-400", children: ["\u00D7", item.quantity] }))] }), _jsxs("p", { className: "truncate text-xs text-slate-400", children: [origin, summary ? ` · ${summary}` : ""] })] }), sheet] }));
}
const EMPTY_BY_COLLECTION = {
    buy_there: "אין פריטים מסומנים ״לקנות שם״",
    missing: "אין פריטים שחסר להם שיוך או כיסוי",
    open: "הכול נארז 🎉",
};
/**
 * A smart-collection reference list. Items are NOT copied — each row references
 * the original item by location, so toggling and editing mutate the source. The
 * origin category/subsection is shown under each item.
 */
export function CollectionView({ content, collectionId, participants, myDisplayName, myParticipantId, onUpdateItem, onBack, }) {
    const [detail, setDetail] = useState(null);
    const def = getCollectionDef(collectionId);
    // Flatten matching items across all sections, deduped by item id.
    const seen = new Set();
    const located = [];
    if (def) {
        for (const section of content.sections) {
            const ctx = {
                sectionKind: section.sectionKind,
                myParticipantId,
                myDisplayName,
            };
            for (const sub of section.subsections) {
                for (const item of sub.items) {
                    if (!def.predicate(item, ctx))
                        continue;
                    if (seen.has(item.id))
                        continue;
                    seen.add(item.id);
                    located.push({
                        item,
                        sectionId: section.id,
                        subId: sub.id,
                        sectionTitle: section.title,
                        sectionIcon: section.icon,
                        subTitle: sub.title,
                        sectionKind: section.sectionKind,
                    });
                }
            }
        }
    }
    // Keep the open detail modal pointed at the live item after edits/toggles —
    // an edit (e.g. clearing "buy there") can drop the item from the filtered
    // list, so look it up directly in content rather than in `located`.
    let liveDetail = null;
    if (detail) {
        for (const section of content.sections) {
            for (const sub of section.subsections) {
                const found = sub.items.find((i) => i.id === detail.item.id);
                if (found) {
                    liveDetail = {
                        item: found,
                        sectionId: section.id,
                        subId: sub.id,
                        sectionTitle: section.title,
                        sectionIcon: section.icon,
                        subTitle: sub.title,
                        sectionKind: section.sectionKind,
                    };
                }
            }
        }
    }
    return (_jsxs("div", { className: "space-y-3", dir: "rtl", children: [_jsxs("button", { onClick: onBack, className: "flex items-center gap-1 text-xs text-slate-500 hover:text-indigo-600", children: [_jsx("svg", { width: "14", height: "14", viewBox: "0 0 16 16", fill: "none", "aria-hidden": true, children: _jsx("path", { d: "M6 4l4 4-4 4", stroke: "currentColor", strokeWidth: "1.5", strokeLinecap: "round", strokeLinejoin: "round" }) }), "\u05D7\u05D6\u05E8\u05D4 \u05DC\u05D0\u05D5\u05E1\u05E4\u05D9\u05DD"] }), _jsxs("div", { className: "flex items-center gap-2", children: [def && (_jsx("span", { className: "text-xl", "aria-hidden": true, children: def.icon })), _jsx("h2", { className: "text-base font-bold text-slate-800", children: def?.title ?? "אוסף" }), _jsx("span", { className: "rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-500", children: located.length })] }), located.length === 0 ? (_jsx("div", { className: "rounded-2xl bg-white border border-slate-100 shadow-sm px-6 py-8 text-center text-sm text-slate-400", children: EMPTY_BY_COLLECTION[collectionId] ?? "אין פריטים באוסף הזה" })) : (_jsx("ul", { className: "space-y-2", children: located.map((loc) => (_jsx(CollectionRow, { loc: loc, participants: participants, myDisplayName: myDisplayName, myParticipantId: myParticipantId, onUpdate: (patch) => onUpdateItem(loc.sectionId, loc.subId, loc.item.id, patch), onOpenDetail: () => setDetail(loc) }, loc.item.id))) })), liveDetail && (_jsx(ItemDetailModal, { item: liveDetail.item, sectionTitle: liveDetail.sectionTitle, subsectionTitle: liveDetail.subTitle, sectionKind: liveDetail.sectionKind, participants: participants, myDisplayName: myDisplayName, myParticipantId: myParticipantId, onUpdate: (patch) => onUpdateItem(liveDetail.sectionId, liveDetail.subId, liveDetail.item.id, patch), onClose: () => setDetail(null) }))] }));
}
